import type {
  HTMLAttributes,
  MouseEvent,
  ReactNode,
  TableHTMLAttributes,
  ThHTMLAttributes,
  TdHTMLAttributes,
} from "react";

function classes(base: string, className?: string): string {
  return [base, className].filter(Boolean).join(" ");
}

export function Table({
  className,
  ...props
}: TableHTMLAttributes<HTMLTableElement>) {
  return <table className={classes("ui-table", className)} {...props} />;
}

export function TableContainer({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={classes("ui-table-container", className)} {...props} />
  );
}

export function TableHead(props: HTMLAttributes<HTMLTableSectionElement>) {
  return <thead {...props} />;
}

export function TableBody(props: HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody {...props} />;
}

export function TableRow(props: HTMLAttributes<HTMLTableRowElement>) {
  return <tr {...props} />;
}

export function ClickableTableRow({
  href,
  onActivate,
  className,
  ...props
}: Omit<
  HTMLAttributes<HTMLTableRowElement>,
  "onClick" | "onAuxClick" | "onKeyDown"
> & {
  href?: string;
  onActivate: () => void;
}) {
  const handleClick = (event: MouseEvent<HTMLTableRowElement>) => {
    if (
      event.defaultPrevented ||
      (event.target instanceof Element &&
        event.target.closest("a, button, input, select, textarea, label"))
    ) {
      return;
    }
    if (
      href &&
      (event.button === 1 ||
        (event.button === 0 && (event.metaKey || event.ctrlKey)))
    ) {
      event.preventDefault();
      window.open(href, "_blank", "noopener,noreferrer");
      return;
    }
    if (event.button === 0) onActivate();
  };

  return (
    <tr
      {...props}
      className={classes("ui-table-row--clickable", className)}
      tabIndex={0}
      onClick={handleClick}
      onAuxClick={handleClick}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onActivate();
        }
      }}
    />
  );
}

export function TableHeader(props: ThHTMLAttributes<HTMLTableCellElement>) {
  return <th scope="col" {...props} />;
}

export function SortableTableHeader({
  children,
  direction,
  onSort,
  ...props
}: Omit<ThHTMLAttributes<HTMLTableCellElement>, "aria-sort" | "children"> & {
  children: ReactNode;
  direction: "asc" | "desc" | null;
  onSort: () => void;
}) {
  return (
    <th
      {...props}
      aria-sort={
        direction === "asc"
          ? "ascending"
          : direction === "desc"
            ? "descending"
            : "none"
      }
    >
      <button type="button" className="ui-table-sort" onClick={onSort}>
        <span>{children}</span>
        <span className="ui-table-sort__direction" aria-hidden="true">
          {direction === "asc" ? "↑" : direction === "desc" ? "↓" : "↕"}
        </span>
      </button>
    </th>
  );
}

export function TableCell(props: TdHTMLAttributes<HTMLTableCellElement>) {
  return <td {...props} />;
}
